import {
  type ChainKey,
  type Chains,
  isActiveVaultChainKey,
} from '@infinex/chain-config';
import type { Custody, Datasets } from '@infinex/types';
import {
  getWalletsV2MainWallet,
  getWalletsV2MappedChainAddresses,
} from '@infinex/types';
import { useDeepMemo } from '@infinex/ui-utils/react';
import { useActiveFrontendChains, useDataset } from '@infinex/user';

import type { AccountAddress } from '../../../types/index';

export type AccountAddressesFilters = {
  custody?: Custody[];
  legacyWalletHandling?: 'include' | 'exclude' | 'only'; // default: 'exclude'
  chain?: ChainKey[];
  walletIds?: string[];
  includeEoaOnAllEvmChains?: boolean;
  /**
   * Exact (walletId, chain) pairs. When provided (non-empty), this replaces
   * the `chain`/`walletIds` filters above rather than combining with them —
   * those two are OR-matched independently against each address row, which
   * cross-matches a wallet id against every listed chain instead of just its
   * own. That's wrong for callers with disjoint per-wallet chain slices (e.g.
   * a safe group's members, where the same anchor wallet id can have
   * deployments split across different groups on different chains).
   */
  walletChainPairs?: { walletId: string; chain: ChainKey }[];
};

/**
 * Returns a list chain-level addresses for the current user with optional filters.
 *
 * 1. EVM chain vaults
 * 2. SVM chain vault
 * 3. Near intents (EVM ops key)
 * 4. WalletsV2 dataset: Infinex-generated SCA and EOA addresses + Imported EOAs
 *
 * TODO: should be renamed to `useCustodyChainAddresses`
 */
export const useAccountAddresses = (
  opts?: AccountAddressesFilters
): AccountAddress[] => {
  const activeChains = useActiveFrontendChains({
    applyCustomOrder: true,
  });

  const { data: user, status: userStatus } = useDataset('user');
  const { data: walletsV2, status: walletsV2Status } = useDataset('walletsV2');

  return useDeepMemo(() => {
    if (
      userStatus !== 'loaded' ||
      !user ||
      walletsV2Status !== 'loaded' ||
      !walletsV2
    ) {
      return [];
    }

    const infinexMainWalletId = getWalletsV2MainWallet(walletsV2)?.id;

    if (!infinexMainWalletId) {
      // Assumption: No main wallet => no wallets at all. See the comment on the deepest underlying util for more reasoning
      return [];
    }

    // Get non-wallet addresses common between single-wallet and multi-wallet
    const nonWalletAddresses = getNonWalletAddresses(user, activeChains);

    // 4. Add wallet addresses based on dataset version
    let walletAddresses: AccountAddress[] = getWalletsV2MappedChainAddresses({
      walletsV2,
      activeChains,
      options: {
        includeEoaOnAllEvmChains: opts?.includeEoaOnAllEvmChains,
      },
    }).map((w) => ({
      address: w.address,
      chain: w.chain,
      custody: w.custody,
      isLegacy: w.isLegacy,
      kind: w.kind,
      walletId: w.walletId,
      label: w.label,
      isImported: w.isImported,
    }));

    const allAddresses = [...nonWalletAddresses, ...walletAddresses];

    // Apply filters if provided
    if (opts) {
      const includeVaults = opts.custody?.includes('vault');

      return allAddresses.filter(
        (a) =>
          (opts.custody && opts.custody.length > 0
            ? opts.custody.includes(a.custody)
            : true) &&
          (opts.walletChainPairs && opts.walletChainPairs.length > 0
            ? opts.walletChainPairs.some(
                (pair) =>
                  pair.walletId === (a.walletId ?? '') && pair.chain === a.chain
              )
            : (opts.chain && opts.chain.length > 0
                ? opts.chain.includes(a.chain)
                : true) &&
              (opts.walletIds && opts.walletIds.length > 0
                ? opts.walletIds.includes(a.walletId ?? '') ||
                  (a.custody === 'vault' && includeVaults) // Always include vaults if custody filter includes vaults, because they have no walletId
                : true)) &&
          matchesLegacyWalletHandling(a, opts.legacyWalletHandling ?? 'exclude')
      );
    }

    return allAddresses;
  }, [userStatus, user, walletsV2Status, walletsV2, activeChains, opts]);
};

/**
 * Get Infinex-generated addresses which aren't in the walletsV2 dataset:
 * 1. EVM chain vaults (many)
 * 2. SVM chain vault (1)
 */
function getNonWalletAddresses(
  user: Datasets['user'],
  activeChains: Chains
): AccountAddress[] {
  const addresses: AccountAddress[] = [];

  Object.entries(activeChains).forEach(([chainKey, chain]) => {
    const runtime = chain.runtime;
    const evmVaultAddress = user.addresses.evmAccountAddress;
    const solanaVaultAddress = user.addresses.solanaDepositAddress;

    // Vaults are not actually chain specific by address,
    // but to unify the format here we make them so
    if (
      isActiveVaultChainKey(chainKey as ChainKey) &&
      runtime === 'evm' &&
      evmVaultAddress
    ) {
      addresses.push({
        address: evmVaultAddress,
        chain: chainKey as ChainKey,
        custody: 'vault',
        walletId: null,
        label: 'Vaults',
        isImported: false,
      });
    }

    if (
      isActiveVaultChainKey(chainKey as ChainKey) &&
      runtime === 'svm' &&
      solanaVaultAddress
    ) {
      addresses.push({
        address: solanaVaultAddress,
        chain: chainKey as ChainKey,
        custody: 'vault',
        walletId: null,
        label: 'Vaults',
        isImported: false,
      });
    }
  });

  return addresses;
}

function matchesLegacyWalletHandling(
  address: AccountAddress,
  legacyWalletHandling: string
): boolean {
  if (address.custody !== 'wallet') {
    return true; // Not a wallet address, include by default
  }

  switch (legacyWalletHandling) {
    case 'include':
      return true;
    case 'only':
      return !!address.isLegacy;
    case 'exclude':
      return !address.isLegacy;
    default:
      throw new Error(
        `Invalid legacyWalletHandling option: ${legacyWalletHandling}`
      );
  }
}
